import { computed, Injectable, signal } from '@angular/core';
import { setUserBetTypeOdd, updateUserSchedule } from '@libs/store/auth/actions';
import {
  addBet,
  addDrawsTable,
  addDub,
  addDubFinished,
  addPendingBets,
  removeBetDub,
  setBetReverseMode,
  setSchedule,
  updateBetAmount,
  updateBetmultiple,
  updateBetNumbers,
  updateBetTypeToBet,
  updateOdd,
} from '@libs/store/betting/actions';
import {
  selectBet,
  selectBetBt,
  selectDrawsTable,
  selectDub,
  selectDubBets,
  selectDubDraw,
  selectDubEvaluated,
  selectDubFinished,
  selectDubFinishedBets,
  selectDubFinishedDraw,
  selectEthBlocks,
  selectLastBetting,
  selectNextDraws,
  selectPendingBets,
  selectSchedule,
} from '@libs/store/betting/selectors';
import { addBetTicket, addBookmarks, removeBetTicket, resetTicket } from '@libs/store/bookmarks/actions';
import { selectBookmarks, selectTicket } from '@libs/store/bookmarks/selectors';
import { addModal } from '@libs/store/modal/actions';
import { IModal } from '@libs/store/modal/model';
import { setOverlayBookmarks } from '@libs/store/settings/actions';
import { selectOverlayBookmarks } from '@libs/store/settings/selectors';
import { addToast } from '@libs/store/toast/actions';
import { selectBalance } from '@libs/store/wallet/selectors';
import {
  BET_TYPE_DENOMINATION,
  DRAW_SCHEDULE,
  IBet,
  IBetType,
  IDrawSchedule,
  IDrawTable,
  IDrawUserBets,
  IEthBlock,
  IUserTicket,
  ODD_TYPE,
} from '@libs/interfaces';
import { IToast } from '@libs/store/toast/model';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StoreBichoService {
  bet = signal<IBet>(null);
  bt = computed<BET_TYPE_DENOMINATION>(() => this.bet()?.betType);
  isCombinada = computed(() => this.bt() == BET_TYPE_DENOMINATION.TDC || this.bt() == BET_TYPE_DENOMINATION.DDC);
  constructor(private store: Store) {
    this.selectBet().subscribe((bet: IBet) => this.bet.set(bet));
  }
  /**
   * Sets the bet reverse mode in the store.
   * @param reverse_bet - The reverse bet mode.
   */
  setBetReverseMode(reverse_bet: boolean) {
    this.store.dispatch(setBetReverseMode({ reverse_bet }));
  }
  /**
   * Selects the current bet from the store.
   * @returns An observable of the current bet.
   */
  selectBet(): Observable<IBet> {
    return this.store.select(selectBet);
  }
  /**
   * Selects the current balance from the store.
   * @returns An observable of the current balance.
   */
  selectBalance() {
    return this.store.select(selectBalance);
  }

  /**
   * Selects the pending bets from the store.
   * @returns An observable of the pending bets.
   */
  selectPendingBets() {
    return this.store.select(selectPendingBets);
  }

  /**
   * Dispatches an action to fetch Ethereum blocks.
   */
  fetchEthBlocks() {
    this.store.dispatch({ type: '[Betting] fetch eth blocks' });
  }

  /**
   * Selects the current dub from the store.
   * @returns An observable of the current dub.
   */
  selectDub() {
    return this.store.select(selectDub);
  }

  /**
   * Selects the draws table from the store.
   * @returns An observable of the draws table.
   */
  selectDrawsTable() {
    return this.store.select(selectDrawsTable);
  }

  /**
   * Adds a dub to the store.
   * @param dub - The dub to be added.
   */
  addDub(dub) {
    this.store.dispatch(addDub({ dub }));
  }

  /**
   * Selects the last betting from the store.
   * @returns An observable of the last betting.
   */
  selectLastBetting() {
    return this.store.select(selectLastBetting);
  }

  /**
   * Selects the next draws from the store.
   * @returns An observable of the next draws.
   */
  selectNextDraws() {
    return this.store.select(selectNextDraws);
  }

  /**
   * Dispatches an action to load the next draws.
   */
  loadNextDraws() {
    this.store.dispatch({ type: '[Betting] fetch next draws' });
  }

  /**
   * Update the bet type and odd.
   * @param bt - The bet type to be updated.
   * @param odd - The odd to be updated.
   */
  updateBetTypeToBet(bt: IBetType, odd: ODD_TYPE): void {
    this.store.dispatch(updateBetTypeToBet({ bt, odd }));
  }

  /**
   * Update the odd.
   * @param odd - The odd to be updated.
   */
  updateOdd(odd: ODD_TYPE): void {
    // Dispatch an action to update the odd
    this.store.dispatch(updateOdd({ odd }));
  }

  /**
   * Dispatches an action to add a bet to the store.
   * @param bet - The bet to be added.
   */
  addBet(bet: IBet): void {
    this.store.dispatch(addBet({ bet }));
  }

  /**
   * Selects the current bet type from the store.
   * @returns An observable of the current bet type.
   */
  selectBetBt(): Observable<BET_TYPE_DENOMINATION> {
    return this.store.select(selectBetBt);
  }

  /**
   * Selects the current ticket from the store.
   * @returns An observable of the current ticket.
   */
  selectTicket(): Observable<any> {
    return this.store.select(selectTicket);
  }

  /**
   * Updates the bet amount in the store.
   * @param amount - The new bet amount.
   */
  updateBetAmount(amount: number): void {
    this.store.dispatch(updateBetAmount({ amount }));
  }

  /**
   * Updates the bet numbers in the store.
   * @param numbers - The new bet numbers.
   */
  updateBetNumbers(numbers: string[]): void {
    this.store.dispatch(updateBetNumbers({ numbers }));
  }

  /**
   * Updates the bet multiple in the store.
   * @param multiple - The new bet multiple.
   */
  updateBetmultiple(multiple: string[][]): void {
    this.store.dispatch(updateBetmultiple({ multiple }));
  }

  /**
   * Adds pending bets to the store.
   * @param bets - The pending bets to be added.
   */
  addPendingBets(bets: IBet[]): void {
    this.store.dispatch(addPendingBets({ bets }));
  }

  /**
   * Loads the last betting data by dispatching an action to the store.
   */
  loadLastBetting(): void {
    this.store.dispatch({ type: '[Betting] on start app' });
  }

  /**
   * Selects the Ethereum blocks from the store.
   * @returns An observable of the Ethereum blocks.
   */
  selectEthBlocks(): Observable<IEthBlock[]> {
    return this.store.select(selectEthBlocks);
  }

  /**
   * Selects the dub bets from the store.
   * @returns The selected dub bets.
   */
  selectDubBets() {
    return this.store.select(selectDubBets);
  }

  /**
   * Selects the dub draw from the store.
   * @returns The selected dub draw.
   */
  selectDubDraw() {
    return this.store.select(selectDubDraw);
  }

  /**
   * Selects the evaluated dub from the store.
   * @returns The selected evaluated dub.
   */
  selectDubEvaluated() {
    return this.store.select(selectDubEvaluated);
  }

  /**
   * Selects the finished dub bets from the store.
   * @returns The selected finished dub bets.
   */
  selectDubFinishedBets() {
    return this.store.select(selectDubFinishedBets);
  }

  /**
   * Selects the finished dub draw from the store.
   * @returns The selected finished dub draw.
   */
  selectDubFinishedDraw() {
    return this.store.select(selectDubFinishedDraw);
  }

  /**
   * Selects the finished dub from the store.
   * @returns An observable of the selected finished dub.
   */
  selectDubFinished(): Observable<IDrawUserBets> {
    return this.store.select(selectDubFinished);
  }

  /**
   * Adds a finished dub to the store.
   * @param dubFinished - The finished dub to add.
   */
  addDubFinished(dubFinished: IDrawUserBets) {
    this.store.dispatch(addDubFinished({ dubFinished }));
  }

  /**
   * Sets the user-selected schedule and updates the store.
   * @param {DRAW_SCHEDULE} schedule - The draw schedule to be set.
   */
  setUserSelectedSchedule(schedule: DRAW_SCHEDULE) {
    this.store.dispatch(updateUserSchedule({ schedule: schedule }));
  }

  /**
   * Selects the current schedule from the store.
   * @returns {Observable<IDrawSchedule>} An observable containing the selected schedule.
   */
  selectSchedule() {
    return this.store.select(selectSchedule);
  }

  /**
   * Adds a toast to the store.
   * @param toast - The toast to be added.
   */
  addToast(toast: IToast) {
    this.store.dispatch(addToast({ toast }));
  }

  /**
   * Sets the schedule in the store.
   * @param schedule - The schedule to be set.
   */
  setSchedule(schedule: IDrawSchedule): void {
    this.store.dispatch(setSchedule({ schedule: schedule }));
  }

  /**
   * Updates the user schedule in the store.
   * @param schedule - The schedule to be updated.
   */
  updateUserSchedule(schedule: DRAW_SCHEDULE): void {
    this.store.dispatch(updateUserSchedule({ schedule: schedule }));
  }

  /**
   * Adds a draws table to the store.
   * @param drawsTable - The draws table to be added.
   */
  addDrawsTable(drawsTable: IDrawTable): void {
    this.store.dispatch(addDrawsTable({ drawsTable: drawsTable }));
  }

  /**
   * Sets the user bet type odd in the store.
   * @param bt - The bet type denomination.
   * @param odd - The odd type.
   */
  setUserBetTypeOdd(bt: BET_TYPE_DENOMINATION, odd: ODD_TYPE): void {
    this.store.dispatch(setUserBetTypeOdd({ bt: bt, odd: odd }));
  }

  /**
   * Removes a bet dub from the store.
   * @param bet - The bet to be removed.
   */
  removeBetDub(bet: IBet): void {
    this.store.dispatch(removeBetDub({ bet: bet }));
  }

  /**
   * Adds a modal to the store.
   * @param modal - The modal to be added.
   */
  addModal(modal: IModal): void {
    this.store.dispatch(addModal({ modal: modal }));
  }

  /**
   * Sets the overlay bookmarks state.
   * @param overlayBookmarks - The state of the overlay bookmarks.
   */
  setOverlayBookmarks(overlayBookmarks: boolean) {
    this.store.dispatch(setOverlayBookmarks({ overlayBookmarks }));
  }

  /**
   * Selects the overlay bookmarks state from the store.
   * @returns An observable of the overlay bookmarks state.
   */
  selectOverlayBookmarks() {
    return this.store.select(selectOverlayBookmarks);
  }

  /**
   * Adds bookmarks to the store.
   * @param bookmarks - The bookmarks to be added.
   */
  addBookmarks(bookmarks: IUserTicket[]): void {
    this.store.dispatch(addBookmarks({ bookmarks }));
  }

  /**
   * Resets the ticket in the store.
   * @param user_id - The user ID associated with the ticket.
   */
  resetTicket(user_id: string): void {
    const ticket = {
      user_id: user_id,
      name: '',
      bets: [],
      job: null,
    };
    this.store.dispatch(resetTicket({ ticket }));
  }

  /**
   * Adds a bet to the current ticket.
   * @param bet - The bet to add.
   */
  addBetTicket(bet: Partial<IBet>) {
    this.store.dispatch(addBetTicket({ bet: bet }));
  }

  /**
   * Removes a bet from the current ticket.
   * @param bet - The bet to remove.
   */
  removeBetTicket(bet: Partial<IBet>) {
    this.store.dispatch(removeBetTicket({ bet: bet }));
  }

  /**
   * Closes the bookmark overlay.
   */
  closeBookmark() {
    this.store.dispatch(setOverlayBookmarks({ overlayBookmarks: false }));
  }

  /**
   * Opens the bookmark overlay.
   */
  openBookmark() {
    this.store.dispatch(setOverlayBookmarks({ overlayBookmarks: true }));
  }

  /**
   * Selects the bookmarks from the store.
   * @returns An observable of the bookmarks.
   */
  selectBookmarks() {
    return this.store.select(selectBookmarks);
  }
}
